import { get, omit } from 'lodash-es';
/* Models */
import { FinancingModel, PATHS } from 'client/data/models/financing';
/* Utils */
import { EventToolbox } from 'client/utils/event-toolbox';
import { isMockId } from 'site-modules/shared/utils/financing/mock-util';
import { getSelectedOffer } from 'site-modules/shared/utils/financing/financing';
import { getDecryptedFinancialFields, getDecryptedPrequalifyData } from 'site-modules/financing/utils/financing-utils';
import { getEncryptedFields } from 'site-modules/financing/utils/encrypting-utils';
import { setFinancingPrequalifiedCookie } from 'site-modules/shared/utils/financing/financing-prequalified-cookie';
/* Constants */
import { ERROR_STATUS, FINANCING_RESPONSE_TYPES } from 'site-modules/shared/constants/financing/financing';
import { FINANCING_EVENTS } from 'site-modules/shared/constants/financing/financing-events';

const savePrequalificationData = async (id, type) => {
  const decryptedPrequalifyData = await getDecryptedPrequalifyData();
  const data = await getDecryptedFinancialFields();
  const isExistingPrequalify = type === FINANCING_RESPONSE_TYPES.PREQUALIFY_SEARCH;

  const prequalificationData = await getEncryptedFields({
    ...decryptedPrequalifyData,
    id,
    firstName: isExistingPrequalify ? get(data, 'lookup.firstName', '') : get(data, 'personal.firstNamePrimary', ''),
    lastName: isExistingPrequalify ? get(data, 'lookup.lastName', '') : get(data, 'personal.lastNamePrimary', ''),
    email: isExistingPrequalify ? get(data, 'lookup.email') : get(data, 'personal.emailPrimary'),
    phoneNumber: isExistingPrequalify
      ? get(data, 'lookup.phoneNumber', '')
      : get(data, 'personal.phoneNumberPrimary', ''),
    zipCode: isExistingPrequalify ? get(data, 'lookup.zip', '') : get(data, 'residence.zipPrimary', ''),
  });

  setFinancingPrequalifiedCookie(JSON.stringify(prequalificationData));
};

export const saveToInsider = async ({ startInsiderAction }, vehicle) => {
  EventToolbox.fireCustomEvent(FINANCING_EVENTS.SAVE_VEHICLE, { vin: vehicle?.vin });
  await startInsiderAction();
};

export const saveOffers = async ({
  offers,
  options: { isAllowBypassApiGateway },
  handlers: { setModelValue, setFinancingOffers },
  vehicle,
}) => {
  if (setFinancingOffers) {
    setFinancingOffers(offers);
  }
  await setModelValue(PATHS.FINANCING_OFFERS({ vin: vehicle?.vin, isAllowBypassApiGateway }), FinancingModel, offers);
};

export async function handleResponse(data) {
  const { status, type, offers, additionalData, paymentData, handlers, prequalificationApplicationId, options } = data;
  const { showErrorPage } = handlers;

  if (status === ERROR_STATUS.SYSTEM_ERROR) {
    EventToolbox.fireCustomEvent(FINANCING_EVENTS.API_SYSTEM_ERROR_V2, {
      financingResponse: data,
    });
  }

  switch (type) {
    case FINANCING_RESPONSE_TYPES.OFFERS_POLLING:
    case FINANCING_RESPONSE_TYPES.PREQUALIFY_SEARCH: {
      const { isMatchingVIN, isPreVinFlow } = additionalData || {};
      const { vehicle } = paymentData || {};
      const { redirectPage, sendConsumerEmail, setQualifiedStatus } = handlers;
      if (prequalificationApplicationId) {
        await savePrequalificationData(prequalificationApplicationId, type);
      }

      if (
        type === FINANCING_RESPONSE_TYPES.OFFERS_POLLING &&
        [ERROR_STATUS.APPROVED, ERROR_STATUS.VEHICLE_NOT_FOUND].includes(status)
      ) {
        EventToolbox.fireCustomEvent(FINANCING_EVENTS.PREQUAL_NEWAPP_APPROVAL, {
          vehicle,
          isMatchingVIN,
          isPreVinFlow,
        });
      }

      if (status === ERROR_STATUS.APPROVED) {
        if (!isPreVinFlow) {
          if (isMatchingVIN) {
            await saveOffers({ offers, options, handlers, vehicle });
          }
          if (!isMockId(prequalificationApplicationId)) {
            await saveToInsider(handlers, vehicle);
            await sendConsumerEmail(status);
          }
        } else {
          await sendConsumerEmail(status);
        }

        redirectPage();
      } else {
        if (type === FINANCING_RESPONSE_TYPES.PREQUALIFY_SEARCH) {
          setQualifiedStatus(status);
        }

        await showErrorPage(status);
      }
      break;
    }
    case FINANCING_RESPONSE_TYPES.UPDATE: {
      if (status === ERROR_STATUS.APPROVED) {
        const { setIsOffersRecalculated } = handlers;
        const { isMatchingVIN } = additionalData || {};
        const selectedOffer = getSelectedOffer(offers);

        await saveOffers({ offers, options, handlers, vehicle: paymentData?.vehicle });

        EventToolbox.fireCustomEvent(FINANCING_EVENTS.CALCULATE_PAYMENTS, {
          monthlyPayment: get(selectedOffer, 'financeInformation.monthlyPayment'),
          selectedTerm: get(selectedOffer, 'financeInformation.loanTerm'),
        });

        EventToolbox.fireCustomEvent(FINANCING_EVENTS.PAYMENT_LIST_PIXEL, { offers, isMatchingVIN });

        const decryptedPrequalifyData = await getDecryptedPrequalifyData();
        const prequalificationData = await getEncryptedFields(omit(decryptedPrequalifyData, ['updatePayments']));

        setFinancingPrequalifiedCookie(JSON.stringify(prequalificationData));
        if (setIsOffersRecalculated) {
          setIsOffersRecalculated(true);
        }
      } else {
        await showErrorPage(status);
      }
      break;
    }
    default: {
      break;
    }
  }
}
